<template>
  <o-modal
    ref="modal"
    :title="$t('components.reservation.actions.recurrence.title')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.reservation.actions.recurrence.confirm'), type: 'confirm', callback: confirm },
    ]"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full flex flex-col"
    :valid="validate"
  >
    <div v-if="staging" class="text-left h-full">
      <o-scroll class="h-full">
        <o-alert
          v-if="isCourse"
          :title="$t('components.reservation.actions.recurrence.course.title')"
          show
          variant="info"
          class="mb-5"
        >
          <p class="text-sm">
            {{ $t('components.reservation.actions.recurrence.course.info', { courses: courseNames }) }}
          </p>
        </o-alert>

        <div v-if="label" class="w-full rounded bg-primary-100 dark:bg-primary-500/10 p-2 mb-5 text-center">
          <p class="text-sm text-primary-700 dark:text-primary-500">
            {{ label }}
          </p>
        </div>

        <recur-reservation v-model:recurrence="staging.recurrence" v-model:preview="staging.preview" />

        <div v-if="removedDates.length" class="mt-6">
          <div class="mb-3">
            <h4 class="font-medium text-gray-800 dark:text-gray-100">
              {{ $t('components.reservation.actions.recurrence.removedDates.title') }}
            </h4>
            <h4 class="text-sm text-gray-600 dark:text-gray-300">
              {{ isCourse
                ? $t('components.reservation.actions.recurrence.removedDates.infoCourse')
                : $t('components.reservation.actions.recurrence.removedDates.info') }}
            </h4>
          </div>

          <div class="bg-gray-100 p-1 rounded-lg border dark:bg-gray-800/30">
            <div v-for="date in removedDates" :key="date.date" class="p-2 flex items-center justify-between border-b last:border-b-0">
              <p class="text-gray-700 text-sm dark:text-gray-200">
                {{ $date.format($date.utc(new Date(date.date * 1000)), $date.presets.long) }}
              </p>

              <o-chip variant="danger" flat>
                {{ $t('components.reservation.actions.recurrence.removedDates.willBeDeleted') }}
              </o-chip>
            </div>
          </div>
        </div>
      </o-scroll>
    </div>

    <o-dialog
      ref="confirmDialog"
      :portal="portal"
      :title="$t('components.reservation.actions.recurrence.confirmChanges.title')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('components.reservation.actions.recurrence.confirmChanges.confirm'), type: 'confirm' },
      ]"
    >
      <div class="text-left space-y-3">
        <p v-if="isCourse" class="text-sm text-gray-600 dark:text-gray-300">
          {{ $t('components.reservation.actions.recurrence.confirmChanges.course', { courses: courseNames }) }}
        </p>

        <template v-if="addedDates.length">
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ isCourse
              ? $t('components.reservation.actions.recurrence.confirmChanges.addedCourse', { count: addedDates.length })
              : $t('components.reservation.actions.recurrence.confirmChanges.added', { count: addedDates.length }) }}
          </p>
          <ul class="text-sm text-gray-700 dark:text-gray-200 list-disc list-inside">
            <li v-for="date in addedDates" :key="date.date">
              {{ $date.format($date.utc(new Date(date.date * 1000)), $date.presets.long) }}
            </li>
          </ul>
        </template>

        <template v-if="removedDates.length">
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ isCourse
              ? $t('components.reservation.actions.recurrence.confirmChanges.removedCourse', { count: removedDates.length })
              : $t('components.reservation.actions.recurrence.confirmChanges.removed', { count: removedDates.length }) }}
          </p>
          <ul class="text-sm text-gray-700 dark:text-gray-200 list-disc list-inside">
            <li v-for="date in removedDates" :key="date.date">
              {{ $date.format($date.utc(new Date(date.date * 1000)), $date.presets.long) }}
            </li>
          </ul>
        </template>
      </div>
    </o-dialog>
  </o-modal>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import { StagingRecurrence } from '@/components/reservation/create/provider'

import RecurReservation from '@/components/recurrence/elements/Reservation.vue'

import {
  OAlert,
} from '@/components/elements'

import { RecurrenceRequest, type RecurrenceSelectedDate } from '@/_api/models/recurrence'

interface Props {
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<{
  'update:reservation': [value: any]
  'success': [value: any]
}>()

const { $api, $notification, $i18n } = useNuxtApp()

const attrs = useAttrs()

const modal = ref<any>(null)
const confirmDialog = ref<any>(null)

const staging = ref<any>(null)
const reservation = ref<any>(null)
const label = ref('')

const portal = computed(() => (attrs.portal as string) ?? null)

const courseRunReservations = computed<any[]>(() => reservation.value?.courseRunReservations ?? [])
const isCourse = computed(() => courseRunReservations.value.length > 0)
const courseNames = computed(() => {
  const names = courseRunReservations.value
    .map((courseRunReservation: any) => {
      const courseRun = courseRunReservation.courseRun
      const name = courseRun?.course?.name

      if (!name) {
        return null
      }

      // Show "course name - run information" so the specific run is identifiable.
      return courseRun?.information ? `${name} - ${courseRun.information}` : name
    })
    .filter(Boolean)

  return [...new Set(names)].join(', ')
})

const removedDates = computed<RecurrenceSelectedDate[]>(() => staging.value?.preview?.recurrence?.removedDates ?? [])
const addedDates = computed<RecurrenceSelectedDate[]>(() => staging.value?.preview?.recurrence?.addedDates ?? [])

const fetchPreview = debounce(async () => {
  const recurrence = staging.value.recurrence
  const reservationVal = reservation.value

  try {
    const request = RecurrenceRequest.create({ recurrence, reservation: reservationVal })
    const response = await $api.booking.previewRecurrence(request)

    if (response.data) {
      label.value = request.recurrence.label
      staging.value.preview = response.data
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }
}, 1000)

watch(
  () => staging.value?.recurrence,
  () => {
    fetchPreview()
  },
  { deep: true },
)

function open(res: any) {
  const stagingVal = new StagingRecurrence()

  if (res) {
    reservation.value = res
    stagingVal.fromReservation(res)
  }

  staging.value = stagingVal

  modal.value.open()
}

async function confirm() {
  if (isCourse.value || removedDates.value.length || addedDates.value.length) {
    const response = await confirmDialog.value.open()

    if (response !== 'confirm') {
      return false
    }
  }

  const reservationVal = reservation.value
  const recurrence = staging.value.recurrence
  const preview = staging.value.preview

  const selectedDates = preview.recurrence.selectedDates

  const datesWithConflicts = selectedDates
    .filter((date: any) => date.hasConflicts)

  try {
    const request = RecurrenceRequest.create({ recurrence })
    request.recurrence.datesWithConflicts = datesWithConflicts
    const response = await $api.booking.recurrence(reservationVal.objectID(), request)
    const updatedReservation = response.data

    if (updatedReservation) {
      $notification.toast({
        title: $i18n.t('components.reservation.actions.recurrence.success'),
        text: $i18n.t('components.reservation.actions.recurrence.successInfo', { readable: label.value }),
        variant: 'success',
      })
      emit('update:reservation', updatedReservation)
      emit('success', updatedReservation)
      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }
}

function validate() {
  const preview = staging.value?.preview

  if (!preview) {
    return false
  }

  const selectedDates = preview.recurrence.selectedDates

  const invalidDates = selectedDates
    .filter((date: any) => date.hasConflicts)
    .some((date: any) => !date.option)

  if (invalidDates) {
    $notification.error({
      errors: [$i18n.t('components.reservation.actions.recurrence.error')],
    })

    return false
  }

  return true
}

defineExpose({ open })
</script>
